/**
 * #750 — what a drag that starts in Papan carries: one opaque code.
 *
 * Any page a drag crosses can read the drag's type names during `dragover`, and
 * the page it is dropped on can read every value. An app on pd.app, or (on an
 * engine that delivers the drag natively) an app frame inside Papan, is such a
 * page — and since #605 people are invited to drop drive items onto apps. So
 * nothing on the drag may identify anything: no file, folder or tenant id, no
 * `/file/<id>` link (CLAUDE.md "File handoff — bytes only").
 *
 * The payload (`{ tenantId, files }`) is written here, in Papan's own origin —
 * this module for the same tab, ONE localStorage entry for Papan's other tabs —
 * under a random code, and only the code goes on the drag. Papan's drop targets
 * look the code up; another origin cannot read Papan's storage (browsers
 * partition it by origin), so the code is meaningless there on every engine.
 * `tenantId` stays in the payload: Papan's targets refuse a move from another
 * tenant's tab with it.
 *
 * localStorage, not BroadcastChannel: the circular-move hint runs on
 * `dragover`, when values are hidden and the hovered tab must PULL the payload
 * synchronously. One entry, not one per drag: a person drags one thing at a
 * time; a drop accepts it only when the code on the drag matches, and it
 * expires, so a leftover from a crashed tab is never taken for a new drag.
 *
 * The ONE writer and reader of a Papan drag: no component touches the type or
 * the storage entry itself (`no-ids-on-drags.test.ts`). The app handoff token
 * (`HANDOFF_DRAG_MIME`, `offerDragToApps`) is separate and untouched.
 *
 * The one exception to "no id on a drag" is the hidden link gesture (owner
 * decision, 2026-10-06): Alt (Option on a Mac) held when the drag STARTS, on
 * exactly one file, adds its `/file/<id>` link — for chat or a new browser tab.
 * The browser can't be told which page may read it, so the link goes only
 * where the person deliberately takes it.
 */

/** The one drag type a Papan drag carries (its value: the code). */
export const PAPAN_DRAG_MIME = 'application/x-papan-drag'

export interface PapanDragItem { id: string; name: string; isFolder: boolean }
/** `tenantId` is absent for a drag that names none (an editor tab, an app tile). */
export interface PapanDragPayload { tenantId?: string; files: PapanDragItem[] }

export interface PapanDragOptions {
  /**
   * The hidden link gesture: pass the `dragstart` event's `altKey`. When set and
   * exactly ONE file (not a folder) is dragged, the drag also carries its
   * `/file/<id>` link. Never set it from anything but the key.
   */
  link?: boolean
  /** Put the dragged names on `text/plain` (a text box dropped on gets them). The link, when there is one, wins. */
  names?: boolean
}

const STORE_KEY = 'papan:drag:v1'
/** A drag held longer than this is treated as gone (`dragend` normally clears it). */
const TTL_MS = 10 * 60_000
/** How long the shared entry outlives `dragend`: a drop in ANOTHER tab may still be reading it. */
const CLEAR_AFTER_MS = 5_000

interface DragRecord extends PapanDragPayload { code: string; expiresAt: number }

let current: DragRecord | null = null
let listening = false
/** dragover fires every few ms: parse the stored entry once per distinct value. */
let parsed: { raw: string; rec: DragRecord | null } | null = null
const NONE: ReadonlySet<string> = new Set()

function newCode(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function isItem(v: unknown): v is PapanDragItem {
  const o = v as PapanDragItem | null
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && typeof o.isFolder === 'boolean'
}

function isRecord(v: unknown): v is DragRecord {
  const o = v as DragRecord | null
  return !!o && typeof o.code === 'string' && typeof o.expiresAt === 'number'
    && (o.tenantId === undefined || typeof o.tenantId === 'string')
    && Array.isArray(o.files) && o.files.every(isItem)
}

function stored(): DragRecord | null {
  let raw: string | null
  try { raw = localStorage.getItem(STORE_KEY) } catch { return null }
  if (!raw) return null
  if (parsed?.raw === raw) return parsed.rec
  let rec: DragRecord | null = null
  try { const v: unknown = JSON.parse(raw); rec = isRecord(v) ? v : null } catch { /* not ours */ }
  parsed = { raw, rec }
  return rec
}

function live(rec: DragRecord | null): rec is DragRecord {
  return !!rec && rec.expiresAt > Date.now()
}

/**
 * The drag being hovered. The shared entry first: every drag writes it, so while
 * storage works it is the NEWEST drag — another tab's included. This tab's own
 * record is the fallback for blocked storage; preferred, it would answer for
 * someone else's drag whenever this tab's dragend never arrived (it never reaches
 * window when the dragged node left the DOM first).
 */
function currentRecord(): DragRecord | null {
  const rec = stored()
  if (live(rec)) return rec
  return live(current) ? current : null
}

function onDragEnd(): void {
  const code = current?.code
  current = null
  if (!code) return
  // Not at once: the drop tab and this one are separate processes, so dragend can
  // reach this tab before the drop handler there has read the entry. The code check
  // keeps a newer drag; the expiry bounds a clear that never runs.
  setTimeout(() => {
    try { if (stored()?.code === code) localStorage.removeItem(STORE_KEY) } catch { /* blocked */ }
  }, CLEAR_AFTER_MS)
}

/** Call from every Papan `dragstart` that drags drive items. */
export function startPapanDrag(dataTransfer: DataTransfer, payload: PapanDragPayload, opts: PapanDragOptions = {}): void {
  const rec: DragRecord = {
    ...(payload.tenantId === undefined ? {} : { tenantId: payload.tenantId }),
    files: payload.files.map(f => ({ id: f.id, name: f.name, isFolder: f.isFolder })),
    code: newCode(),
    expiresAt: Date.now() + TTL_MS,
  }
  current = rec
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(rec))
  } catch {
    // Storage blocked: this tab still reads `current`; another tab must not read an older drag.
    try { localStorage.removeItem(STORE_KEY) } catch { /* blocked */ }
  }
  if (!listening && typeof window !== 'undefined') {
    window.addEventListener('dragend', onDragEnd, true)
    listening = true
  }
  dataTransfer.setData(PAPAN_DRAG_MIME, rec.code)

  const only = rec.files.length === 1 ? rec.files[0]! : null
  if (opts.link && only && !only.isFolder) {
    const url = `${window.location.origin}/file/${only.id}`
    dataTransfer.setData('text/uri-list', url)
    dataTransfer.setData('text/plain', url)
    // Alt still held at the drop asks the OS (Windows) for a LINK drop, which
    // copyMove refuses. 'all' keeps move, so Papan's own targets are unchanged.
    dataTransfer.effectAllowed = 'all'
  } else if (opts.names) {
    dataTransfer.setData('text/plain', rec.files.map(f => f.name).join(', '))
  }
}

/** Whether this drag is a Papan drag — readable during dragenter/dragover. */
export function isPapanDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes(PAPAN_DRAG_MIME)
}

/**
 * The payload of the drag being dropped, or null (not a Papan drag, or one this
 * tab can't resolve). Call it SYNCHRONOUSLY in the drop handler: the source tab
 * clears the drag a few seconds after `dragend`, which follows the drop. A receiver with an
 * "upload the dropped OS files" fallback asks `isPapanDrag` first, so a Papan
 * drag it can't resolve is ignored rather than treated as an upload.
 */
export function readPapanDrag(dataTransfer: DataTransfer): PapanDragPayload | null {
  const code = dataTransfer.getData(PAPAN_DRAG_MIME)
  if (!code) return null
  const rec = current?.code === code && live(current) ? current : stored()
  if (!live(rec) || rec.code !== code) return null
  const files = rec.files.map(f => ({ ...f }))
  return rec.tenantId === undefined ? { files } : { tenantId: rec.tenantId, files }
}

/**
 * The dragged FOLDER ids, during dragover (values are hidden then; only the type
 * name says it is a Papan drag). For the circular-move hint only — advisory, the
 * server refuses a bad move regardless.
 */
export function peekPapanDragFolderIds(dataTransfer: DataTransfer): ReadonlySet<string> {
  if (!isPapanDrag(dataTransfer)) return NONE
  const rec = currentRecord()
  return rec ? new Set(rec.files.filter(f => f.isFolder).map(f => f.id)) : NONE
}
